// Journey Path Configuration
// Maps (goal + experienceLevel) → sections → concrete nodes
// Each path has 4 sections × 5 nodes = 20 nodes total (~3 months of content)

import { IconType } from 'components/Icon';

import {
  CHALLENGE_CATALOG as CC,
  PROBLEM_CATALOG as PRC,
  PROJECT_CATALOG as PC,
} from './journeyContentCatalog';

// ─── Types ───────────────────────────────────────────────────────────────────

export type JourneyNodeType = 'project' | 'challenge' | 'problem' | 'code-frame' | 'prototype' | 'cv';

export type JourneyNodeDifficulty = 'easy' | 'medium' | 'hard' | 'expert';

export interface JourneyNode {
  id: string;
  type: JourneyNodeType;
  difficulty?: JourneyNodeDifficulty;
  label: string;
  screenName?: string;
  subtitle?: string;
}

export interface JourneySection {
  title: string;
  checkpointLabel: string;
  nodes: JourneyNode[];
}

export type JourneyPathKey = `${string}-${string}`;

// ─── Node Builder Helpers ────────────────────────────────────────────────────

const P = (key: keyof typeof PC, id: string): JourneyNode => ({
  id,
  type: 'project',
  difficulty: PC[key].difficulty,
  label: PC[key].title,
  screenName: PC[key].screenName,
  subtitle: `${PC[key].difficulty.charAt(0).toUpperCase() + PC[key].difficulty.slice(1)} Project`,
});

const CH = (key: keyof typeof CC, id: string): JourneyNode => ({
  id,
  type: 'challenge',
  label: CC[key].title,
  screenName: CC[key].screenName,
  subtitle: 'Challenge',
});

const PR = (key: keyof typeof PRC, id: string): JourneyNode => ({
  id,
  type: 'problem',
  difficulty: PRC[key].difficulty,
  label: PRC[key].title,
  screenName: PRC[key].screenName,
  subtitle: `${PRC[key].difficulty.charAt(0).toUpperCase() + PRC[key].difficulty.slice(1)} Problem`,
});

const CF = (label: string, id: string): JourneyNode => ({
  id,
  type: 'code-frame',
  label,
  subtitle: 'Create a code frame',
});

const PT = (label: string, id: string): JourneyNode => ({
  id,
  type: 'prototype',
  label,
  subtitle: 'Create a prototype',
});

const CV = (label: string, id: string): JourneyNode => ({
  id,
  type: 'cv',
  label,
  subtitle: 'Build your CV',
});

// ─── Path Definitions (12 base paths) ────────────────────────────────────────

export const JOURNEY_PATHS: Record<string, JourneySection[]> = {
  // ═══ BUILD PRODUCT ═══

  'build-product-beginner': [
    {
      title: 'First Steps',
      checkpointLabel: 'Learn the Fundamentals',
      nodes: [
        P('todoApp', 'n01'),
        PR('reverseString', 'n02'),
        CH('profileCard', 'n03'),
        P('businessCard', 'n04'),
        PR('twoSum', 'n05'),
      ],
    },
    {
      title: 'Build Something Real',
      checkpointLabel: 'Build Something Real',
      nodes: [
        P('musicPlayer', 'n06'),
        CF('Component Library', 'n07'),
        CH('addToBag', 'n08'),
        PR('palindrome', 'n09'),
        P('calculator', 'n10'),
      ],
    },
    {
      title: 'Level Up Your Builds',
      checkpointLabel: 'Level Up Your Builds',
      nodes: [
        PT('Your Portfolio', 'n11'),
        P('quizApp', 'n12'),
        CH('shoppingList', 'n13'),
        PR('sortArray', 'n14'),
        P('justWeather', 'n15'),
      ],
    },
    {
      title: 'Ship It',
      checkpointLabel: 'Ship Your Product',
      nodes: [
        P('blog', 'n16'),
        PT('SaaS Landing Page', 'n17'),
        CH('pricingPlans', 'n18'),
        P('pokedex', 'n19'),
        CF('Dashboard Widget', 'n20'),
      ],
    },
  ],

  'build-product-intermediate': [
    {
      title: 'Sharpen Your Skills',
      checkpointLabel: 'Sharpen Your Skills',
      nodes: [
        P('calculator', 'n01'),
        CH('taskBoard', 'n02'),
        PR('mergeIntervals', 'n03'),
        CF('Component Library', 'n04'),
        P('quizApp', 'n05'),
      ],
    },
    {
      title: 'Build & Iterate',
      checkpointLabel: 'Build & Iterate',
      nodes: [
        PT('MVP Dashboard', 'n06'),
        P('dashboard', 'n07'),
        CH('pricingPlans', 'n08'),
        PR('maxSubarray', 'n09'),
        CF('Interactive Form', 'n10'),
      ],
    },
    {
      title: 'Scale Your Projects',
      checkpointLabel: 'Scale Your Projects',
      nodes: [
        P('blog', 'n11'),
        PR('groupAnagrams', 'n12'),
        CH('videoPlayer', 'n13'),
        PT('SaaS Landing Page', 'n14'),
        P('notely', 'n15'),
      ],
    },
    {
      title: 'Launch Ready',
      checkpointLabel: 'Launch Ready',
      nodes: [
        P('findMovies', 'n16'),
        CH('invoices', 'n17'),
        PT('Production App', 'n18'),
        P('foodieGo', 'n19'),
        CF('Design System', 'n20'),
      ],
    },
  ],

  'build-product-advanced': [
    {
      title: 'Architect Mode',
      checkpointLabel: 'Master Architecture',
      nodes: [
        P('findMovies', 'n01'),
        CH('dashboardChallenge', 'n02'),
        PT('Design System', 'n03'),
        PR('topKFrequent', 'n04'),
        CF('State Manager', 'n05'),
      ],
    },
    {
      title: 'Scale & Polish',
      checkpointLabel: 'Scale & Polish',
      nodes: [
        P('foodieGo', 'n06'),
        PR('searchRotatedArray', 'n07'),
        CH('meetingSchedule', 'n08'),
        PT('Real-Time Dashboard', 'n09'),
        P('anyChat', 'n10'),
      ],
    },
    {
      title: 'Production Systems',
      checkpointLabel: 'Build Production Systems',
      nodes: [
        CF('API Client Library', 'n11'),
        P('dashboard', 'n12'),
        CH('musicFestival', 'n13'),
        PR('topNFrequentWords', 'n14'),
        PT('Production App', 'n15'),
      ],
    },
    {
      title: 'Ship to Production',
      checkpointLabel: 'Ship to Production',
      nodes: [
        P('gifTok', 'n16'),
        PR('wordBreak', 'n17'),
        CH('brandVisualizer', 'n18'),
        PT('Full-Stack SaaS', 'n19'),
        CF('Open Source Component', 'n20'),
      ],
    },
  ],

  // ═══ LAND A JOB ═══

  'land-job-beginner': [
    {
      title: 'Foundation',
      checkpointLabel: 'Build Your Foundation',
      nodes: [
        PR('isEven', 'n01'),
        PR('reverseString', 'n02'),
        P('todoApp', 'n03'),
        CH('profileCard', 'n04'),
        PR('twoSum', 'n05'),
      ],
    },
    {
      title: 'Build Your Portfolio',
      checkpointLabel: 'Build Your Portfolio',
      nodes: [
        P('musicPlayer', 'n06'),
        PR('palindrome', 'n07'),
        CH('contactUs', 'n08'),
        P('calculator', 'n09'),
        PR('findMax', 'n10'),
      ],
    },
    {
      title: 'Interview Prep',
      checkpointLabel: 'Prepare for Interviews',
      nodes: [
        PR('sortArray', 'n11'),
        P('quizApp', 'n12'),
        CH('notifications', 'n13'),
        PR('sumArray', 'n14'),
        CV('Junior Frontend Developer', 'n15'),
      ],
    },
    {
      title: 'Career Ready',
      checkpointLabel: 'Get Career Ready',
      nodes: [
        P('justWeather', 'n16'),
        PR('factorial', 'n17'),
        CH('jobBoard', 'n18'),
        P('pokedex', 'n19'),
        PR('countVowels', 'n20'),
      ],
    },
  ],

  'land-job-intermediate': [
    {
      title: 'Strengthen Fundamentals',
      checkpointLabel: 'Strengthen Fundamentals',
      nodes: [
        PR('mergeIntervals', 'n01'),
        CH('taskBoard', 'n02'),
        P('calculator', 'n03'),
        PR('threeSum', 'n04'),
        PR('maxSubarray', 'n05'),
      ],
    },
    {
      title: 'Portfolio & Practice',
      checkpointLabel: 'Polish Your Portfolio',
      nodes: [
        P('dashboard', 'n06'),
        PR('groupAnagrams', 'n07'),
        CH('userProfile', 'n08'),
        P('blog', 'n09'),
        CV('Mid-Level React Developer', 'n10'),
      ],
    },
    {
      title: 'Interview Deep Dive',
      checkpointLabel: 'Deep Dive Interviews',
      nodes: [
        PR('productExceptSelf', 'n11'),
        P('notely', 'n12'),
        CH('leaderboards', 'n13'),
        PR('containerMostWater', 'n14'),
        P('findMovies', 'n15'),
      ],
    },
    {
      title: 'Final Stretch',
      checkpointLabel: 'Final Stretch',
      nodes: [
        PR('bestTimeBuySell', 'n16'),
        CH('jobBoard', 'n17'),
        P('foodieGo', 'n18'),
        PR('longestConsecutive', 'n19'),
        PR('findPeakElement', 'n20'),
      ],
    },
  ],

  'land-job-advanced': [
    {
      title: 'Expert Problems',
      checkpointLabel: 'Tackle Expert Problems',
      nodes: [
        PR('topKFrequent', 'n01'),
        CH('dashboardChallenge', 'n02'),
        P('findMovies', 'n03'),
        PR('searchRotatedArray', 'n04'),
        PR('topNFrequentWords', 'n05'),
      ],
    },
    {
      title: 'Showcase Projects',
      checkpointLabel: 'Showcase Your Work',
      nodes: [
        P('foodieGo', 'n06'),
        PR('binaryTreeLevel', 'n07'),
        P('anyChat', 'n08'),
        CV('Senior Full-Stack Engineer', 'n09'),
        CH('meetingSchedule', 'n10'),
      ],
    },
    {
      title: 'System Design',
      checkpointLabel: 'Design Systems',
      nodes: [
        PR('wordBreak', 'n11'),
        P('gifTok', 'n12'),
        CH('exploreFlights', 'n13'),
        PR('subsets', 'n14'),
        PR('permutations', 'n15'),
      ],
    },
    {
      title: 'Ready to Apply',
      checkpointLabel: 'Ready to Apply',
      nodes: [
        P('dashboard', 'n16'),
        PR('longestPalindromic', 'n17'),
        CH('brandVisualizer', 'n18'),
        PR('firstMissingPositive', 'n19'),
        P('codeEditor', 'n20'),
      ],
    },
  ],

  // ═══ LEVEL UP ═══

  'level-up-beginner': [
    {
      title: 'Learn the Basics',
      checkpointLabel: 'Learn the Basics',
      nodes: [
        P('todoApp', 'n01'),
        PR('isEven', 'n02'),
        CH('profileCard', 'n03'),
        PR('reverseString', 'n04'),
        P('businessCard', 'n05'),
      ],
    },
    {
      title: 'Apply Your Knowledge',
      checkpointLabel: 'Apply Your Knowledge',
      nodes: [
        P('musicPlayer', 'n06'),
        PR('twoSum', 'n07'),
        CH('recipe', 'n08'),
        P('colorMemory', 'n09'),
        PR('palindrome', 'n10'),
      ],
    },
    {
      title: 'Growing Stronger',
      checkpointLabel: 'Grow Stronger',
      nodes: [
        CH('imageCarousel', 'n11'),
        P('calculator', 'n12'),
        PR('sortArray', 'n13'),
        P('quizApp', 'n14'),
        CH('shoppingList', 'n15'),
      ],
    },
    {
      title: 'Push Your Limits',
      checkpointLabel: 'Push Your Limits',
      nodes: [
        P('justWeather', 'n16'),
        PR('findMax', 'n17'),
        CH('notifications', 'n18'),
        P('pokedex', 'n19'),
        PR('factorial', 'n20'),
      ],
    },
  ],

  'level-up-intermediate': [
    {
      title: 'Deepen Understanding',
      checkpointLabel: 'Deepen Understanding',
      nodes: [
        P('calculator', 'n01'),
        PR('mergeIntervals', 'n02'),
        CH('taskBoard', 'n03'),
        PR('threeSum', 'n04'),
        P('quizApp', 'n05'),
      ],
    },
    {
      title: 'Complex Challenges',
      checkpointLabel: 'Conquer Challenges',
      nodes: [
        P('dashboard', 'n06'),
        PR('groupAnagrams', 'n07'),
        CH('videoPlayer', 'n08'),
        P('blog', 'n09'),
        PR('maxSubarray', 'n10'),
      ],
    },
    {
      title: 'Advanced Patterns',
      checkpointLabel: 'Master Patterns',
      nodes: [
        CH('invoices', 'n11'),
        P('notely', 'n12'),
        PR('productExceptSelf', 'n13'),
        P('findMovies', 'n14'),
        PR('containerMostWater', 'n15'),
      ],
    },
    {
      title: 'Mastery',
      checkpointLabel: 'Reach Mastery',
      nodes: [
        P('foodieGo', 'n16'),
        PR('bestTimeBuySell', 'n17'),
        CH('musicFestival', 'n18'),
        P('gifTok', 'n19'),
        PR('longestConsecutive', 'n20'),
      ],
    },
  ],

  'level-up-advanced': [
    {
      title: 'Expert Challenges',
      checkpointLabel: 'Take On Expert Challenges',
      nodes: [
        CH('dashboardChallenge', 'n01'),
        PR('topKFrequent', 'n02'),
        P('findMovies', 'n03'),
        PR('searchRotatedArray', 'n04'),
        CH('meetingSchedule', 'n05'),
      ],
    },
    {
      title: 'Architecture & Design',
      checkpointLabel: 'Architect & Design',
      nodes: [
        P('foodieGo', 'n06'),
        PR('binaryTreeLevel', 'n07'),
        CH('brandVisualizer', 'n08'),
        P('anyChat', 'n09'),
        PR('topNFrequentWords', 'n10'),
      ],
    },
    {
      title: 'Deep Algorithms',
      checkpointLabel: 'Conquer Deep Algorithms',
      nodes: [
        PR('wordBreak', 'n11'),
        P('gifTok', 'n12'),
        CH('exploreFlights', 'n13'),
        PR('subsets', 'n14'),
        P('dashboard', 'n15'),
      ],
    },
    {
      title: 'Beyond Mastery',
      checkpointLabel: 'Go Beyond Mastery',
      nodes: [
        PR('permutations', 'n16'),
        CH('qrCodeScanner', 'n17'),
        P('codeEditor', 'n18'),
        PR('longestPalindromic', 'n19'),
        PR('firstMissingPositive', 'n20'),
      ],
    },
  ],

  // ═══ EXPLORE ═══

  'explore-beginner': [
    {
      title: 'Try Everything',
      checkpointLabel: 'Try Everything',
      nodes: [
        P('todoApp', 'n01'),
        CH('profileCard', 'n02'),
        PR('reverseString', 'n03'),
        CF('Animated Button', 'n04'),
        P('ticTacToe', 'n05'),
      ],
    },
    {
      title: 'Go Deeper',
      checkpointLabel: 'Go Deeper',
      nodes: [
        P('characterGenerator', 'n06'),
        PT('Personal Portfolio', 'n07'),
        CH('recipe', 'n08'),
        PR('twoSum', 'n09'),
        P('colorMemory', 'n10'),
      ],
    },
    {
      title: 'Create & Share',
      checkpointLabel: 'Create & Share',
      nodes: [
        CV('Junior Developer', 'n11'),
        P('musicPlayer', 'n12'),
        CH('imageCarousel', 'n13'),
        CF('Social Card', 'n14'),
        PR('palindrome', 'n15'),
      ],
    },
    {
      title: 'Keep Exploring',
      checkpointLabel: 'Keep Exploring',
      nodes: [
        P('calculator', 'n16'),
        CH('notifications', 'n17'),
        PT('Fun Side Project', 'n18'),
        P('quizApp', 'n19'),
        PR('sortArray', 'n20'),
      ],
    },
  ],

  'explore-intermediate': [
    {
      title: 'Broaden Horizons',
      checkpointLabel: 'Broaden Horizons',
      nodes: [
        P('calculator', 'n01'),
        CH('taskBoard', 'n02'),
        CF('Data Visualization', 'n03'),
        PR('mergeIntervals', 'n04'),
        P('justWeather', 'n05'),
      ],
    },
    {
      title: 'Build & Create',
      checkpointLabel: 'Build & Create',
      nodes: [
        PT('Interactive Portfolio', 'n06'),
        P('blog', 'n07'),
        CH('videoPlayer', 'n08'),
        PR('groupAnagrams', 'n09'),
        P('notely', 'n10'),
      ],
    },
    {
      title: 'Push Boundaries',
      checkpointLabel: 'Push Boundaries',
      nodes: [
        CV('Full-Stack Developer', 'n11'),
        CF('Chart Component', 'n12'),
        P('dashboard', 'n13'),
        CH('invoices', 'n14'),
        PR('maxSubarray', 'n15'),
      ],
    },
    {
      title: 'Master Everything',
      checkpointLabel: 'Master Everything',
      nodes: [
        P('findMovies', 'n16'),
        PT('Creative Showcase', 'n17'),
        CH('musicFestival', 'n18'),
        P('gifTok', 'n19'),
        PR('bestTimeBuySell', 'n20'),
      ],
    },
  ],

  'explore-advanced': [
    {
      title: 'Challenge Yourself',
      checkpointLabel: 'Challenge Yourself',
      nodes: [
        P('findMovies', 'n01'),
        CH('dashboardChallenge', 'n02'),
        PR('topKFrequent', 'n03'),
        PT('Design System', 'n04'),
        P('foodieGo', 'n05'),
      ],
    },
    {
      title: 'Create Complex Things',
      checkpointLabel: 'Create Complex Things',
      nodes: [
        CF('Rich Text Editor', 'n06'),
        P('anyChat', 'n07'),
        CH('meetingSchedule', 'n08'),
        PR('searchRotatedArray', 'n09'),
        P('codeEditor', 'n10'),
      ],
    },
    {
      title: 'Leave Your Mark',
      checkpointLabel: 'Leave Your Mark',
      nodes: [
        PT('Open Source Tool', 'n11'),
        CV('Senior Engineer', 'n12'),
        P('gifTok', 'n13'),
        CH('brandVisualizer', 'n14'),
        PR('topNFrequentWords', 'n15'),
      ],
    },
    {
      title: 'Infinite Exploration',
      checkpointLabel: 'Explore the Infinite',
      nodes: [
        P('dashboard', 'n16'),
        PR('wordBreak', 'n17'),
        CH('exploreFlights', 'n18'),
        CF('Animation Library', 'n19'),
        PR('permutations', 'n20'),
      ],
    },
  ],
};

// ─── Focus Area Modifier ─────────────────────────────────────────────────────

/**
 * Apply focus_area modifier to a base journey path.
 * - build: swaps first Problem per section → Code Frame / Prototype (alternating)
 * - career: ensures CV appears in section 1, swaps first Challenge in section 2 → hard Problem
 * - learn: no modification (default — base paths are learn-oriented)
 */
export const applyFocusModifier = (
  sections: JourneySection[],
  focusArea: string
): JourneySection[] => {
  const cloned: JourneySection[] = sections.map((s) => ({
    ...s,
    nodes: s.nodes.map((n) => ({ ...n })),
  }));

  if (focusArea === 'build') {
    for (let si = 0; si < cloned.length; si++) {
      const problemIdx = cloned[si].nodes.findIndex((n) => n.type === 'problem');
      if (problemIdx !== -1) {
        const nodeId = `${cloned[si].nodes[problemIdx].id}-bf`;
        cloned[si].nodes[problemIdx] =
          si % 2 === 0
            ? CF('Interactive Component', nodeId)
            : PT('Creative Project', nodeId);
      }
    }
  }

  if (focusArea === 'career') {
    const hasCv = cloned.some((s) => s.nodes.some((n) => n.type === 'cv'));
    if (!hasCv && cloned.length > 0) {
      const lastIdx = cloned[0].nodes.length - 1;
      const nodeId = `${cloned[0].nodes[lastIdx].id}-cf`;
      cloned[0].nodes[lastIdx] = CV('Your Developer CV', nodeId);
    }

    if (cloned.length > 1) {
      const challengeIdx = cloned[1].nodes.findIndex((n) => n.type === 'challenge');
      if (challengeIdx !== -1) {
        const nodeId = `${cloned[1].nodes[challengeIdx].id}-cf`;
        cloned[1].nodes[challengeIdx] = PR('mergeSortedLists', nodeId);
      }
    }
  }

  return cloned;
};

// ─── Path Retrieval ──────────────────────────────────────────────────────────

/**
 * Get the journey path sections for a user based on their preferences.
 * Falls back to explore-beginner if no matching path found.
 */
export const getJourneyPath = (
  goal?: string | null,
  experienceLevel?: string | null,
  focusArea?: string | null
): JourneySection[] => {
  const key = `${goal || 'explore'}-${experienceLevel || 'beginner'}`;
  const basePath = JOURNEY_PATHS[key] || JOURNEY_PATHS['explore-beginner'];

  if (!focusArea || focusArea === 'learn') return basePath;

  return applyFocusModifier(basePath, focusArea);
};

// ─── Utilities ───────────────────────────────────────────────────────────────

export const getNodeIconType = (type: JourneyNodeType): IconType => {
  switch (type) {
    case 'project': return 'SidebarProjects';
    case 'challenge': return 'SidebarChallenges';
    case 'problem': return 'SidebarPracticeProblems';
    case 'code-frame': return 'SidebarCodeFrames';
    case 'prototype': return 'SidebarPrototypes';
    case 'cv': return 'Profile';
    default: return 'SidebarProjects';
  }
};

/**
 * Get the route for a journey node. Returns specific route when screenName is present.
 */
export const getNodeRoute = (node: JourneyNode): string => {
  if (node.screenName) {
    switch (node.type) {
      case 'project': return `/projects/${node.screenName}`;
      case 'challenge': return `/challenges/${node.screenName}`;
      case 'problem': return `/problems/${node.screenName}`;
      default: break;
    }
  }
  switch (node.type) {
    case 'project': return '/projects';
    case 'challenge': return '/challenges';
    case 'problem': return '/practice-problems';
    case 'code-frame': return '/code-frames';
    case 'prototype': return '/prototypes';
    case 'cv': return '/cv-builder';
    default: return '/projects';
  }
};

/**
 * Extract all unique project screen names from a journey path (for thumbnail fetching).
 */
export const getProjectScreenNames = (sections: JourneySection[]): string[] => {
  const names = new Set<string>();
  for (const section of sections) {
    for (const node of section.nodes) {
      if (node.type === 'project' && node.screenName) {
        names.add(node.screenName);
      }
    }
  }
  return Array.from(names);
};
